import baseService from './base';

/**
 * EG-41 — a classificação não passa pelo save do template.
 *
 * survey-template/edit-template-with-questions soft-deleta a linhagem e recria
 * cada questão com id novo. Se a classificação entrasse por lá, no instante do
 * save o id_question_template pelo qual a propagação filtra as instâncias já
 * teria sido substituído — o CA07 ficaria impossível por construção. Por isso
 * estas chamadas gravam na hora, por id, fora do fluxo de versionamento.
 */

/**
 * O backend recusa com 400 qualquer campo fora da classificação (AD-3). Como
 * quem chama costuma ter um objeto de formulário inteiro em mãos, a poda
 * acontece aqui: é mais barato do que descobrir pelo 400.
 */
const somenteClassificacao = ({ id_pillar, id_theme, gri_ids, propagar }) => ({
  id_pillar: id_pillar ?? null,
  id_theme: id_theme ?? null,
  // GRI-02: aqui `null` NÃO serve de default como nos dois acima. Para o
  // backend, ausente significa "não mexa nos vínculos" e lista vazia significa
  // "desvincule todos" — são coisas diferentes de propósito, e achatar as duas
  // num null faria toda tela que só mexe em pilar apagar os indicadores da
  // questão. Só entra no corpo quem mandou o campo.
  ...(Array.isArray(gri_ids) ? { gri_ids } : {}),
  // CA7.2: desmarcado por padrão. O default vive aqui, e não no componente,
  // para que nenhuma tela nova propague sem alguém ter decidido isso.
  propagar: propagar === true,
});

export const classificarQuestao = async (idQuestionTemplate, valores) => {
  const { data } = await baseService.patch(
    `/question-template/${idQuestionTemplate}`,
    somenteClassificacao(valores)
  );
  return data;
};

export const classificarEmMassa = async ({ ids, ...valores }) => {
  const corpo = somenteClassificacao(valores);

  // GRI-02: no lote, lista vazia não significa nada — aqui a semântica é
  // ACRESCENTAR, e acrescentar nada é não mexer. Mandar `[]` faria o backend
  // aceitar um pedido que não pede coisa alguma, em vez de recusá-lo por não
  // ter pilar, tema nem indicador. No caminho individual é o oposto: lá `[]` é
  // o único jeito de desvincular, e por isso continua sendo enviado.
  if (Array.isArray(corpo.gri_ids) && corpo.gri_ids.length === 0) {
    delete corpo.gri_ids;
  }

  const { data } = await baseService.post('/question-template/classify-bulk', {
    ids,
    ...corpo,
  });
  return data;
};

/**
 * EG-41 — CA04: a classificação sai do lápis do card direto para a API.
 *
 * Grava apenas quando mudou de fato. Cada PATCH escreve uma linha em
 * question_classification_audit e, com o checkbox marcado, pode tocar dezenas
 * de instâncias — salvar "por via das dúvidas" encheria a trilha do AD-9 de
 * linhas onde anterior e novo são iguais, justamente na tabela que existe para
 * responder quem mudou o quê.
 *
 * Devolve null quando não havia o que gravar, para a tela não anunciar uma
 * operação que não aconteceu.
 */
export const salvarClassificacaoSeMudou = async ({
  original = {},
  valores = {},
  propagar = false,
}) => {
  const id = original.id_question_template;
  if (!id) return null;

  const mesmo = (a, b) => (a ?? null) === (b ?? null);
  if (
    mesmo(original.id_pillar, valores.id_pillar) &&
    mesmo(original.id_theme, valores.id_theme) &&
    mesmoConjunto(idsDoGri(original), valores.gri_ids)
  ) {
    return null;
  }

  return classificarQuestao(id, { ...valores, propagar });
};

/**
 * GRI-02: os dois lados da comparação falam formatos diferentes.
 *
 * O que veio da API traz `gri` como lista de objetos (código, nome, série, que
 * é o que o card precisa para pintar a tag); o formulário guarda `gri_ids`, só
 * os ids. Sem normalizar, o "mudou?" comparava `undefined` com um array e dava
 * SEMPRE diferente — abrir e fechar a questão sem mexer em nada gravaria uma
 * linha na trilha.
 */
const idsDoGri = (origem = {}) => {
  if (Array.isArray(origem.gri_ids)) return origem.gri_ids;
  if (Array.isArray(origem.gri)) {
    return origem.gri.map((indicador) => indicador.id_gri_indicator);
  }
  return undefined;
};

/**
 * GRI-02: comparação de CONJUNTO, não de referência nem de ordem.
 *
 * O multiselect devolve array novo a cada clique, e a ordem depende da ordem em
 * que o gestor clicou. Comparar por identidade ou por posição faria toda edição
 * de pilar disparar um PATCH de GRI — e cada PATCH escreve na trilha, que
 * existe justamente para responder quem mudou o quê.
 *
 * Ausente dos dois lados conta como igual; ausente de um lado só, não.
 */
const mesmoConjunto = (a, b) => {
  if (!Array.isArray(a) && !Array.isArray(b)) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;

  const ordenado = (lista) => [...lista].map(Number).sort((x, y) => x - y);
  return ordenado(a).every((valor, indice) => valor === ordenado(b)[indice]);
};

const pluralQuestoes = (total) =>
  `${total} ${total === 1 ? 'questão' : 'questões'}`;

/** CA6.4: o texto de sucesso, com o alcance real da operação. */
export const mensagemDeAplicacao = (resultado = {}) => {
  const total = resultado.questoes_classificadas ?? 0;

  // GRI-02 — CA3.3: o texto nomeia o que de fato foi aplicado. Anunciar "pilar
  // e tema" num lote que só mexeu em indicador seria mentir para o gestor sobre
  // o que acabou de acontecer com N questões.
  const aplicouGri = (resultado.gri_aplicados ?? 0) > 0;
  const aplicouClassificacao =
    resultado.id_pillar != null || resultado.id_theme != null;

  // "Pilar e tema" segue sendo o padrão: só troca quando o lote foi SÓ de
  // indicador. Sem esta assimetria, uma resposta sem os campos de
  // classificação (o formato que a EG-41 já devolvia) passaria a anunciar GRI
  // que ninguém aplicou.
  const oQue =
    aplicouGri && !aplicouClassificacao
      ? 'Indicadores GRI aplicados'
      : aplicouGri
      ? 'Pilar, tema e indicadores GRI aplicados'
      : 'Pilar e tema aplicados';

  let mensagem = `${oQue} a ${pluralQuestoes(total)}`;

  const propagacao = resultado.propagacao;
  if (propagacao?.instancias_atualizadas > 0) {
    mensagem += `, e propagados para ${pluralQuestoes(
      propagacao.instancias_atualizadas
    )} em questionários existentes`;
  }

  // GRI-02: contado à parte porque um pedido pode ser SÓ de indicador — somar
  // aos dois números esconderia qual das duas coisas alcançou o questionário.
  const comGri = propagacao?.instancias_com_gri ?? 0;
  if (comGri > 0 && !(propagacao?.instancias_atualizadas > 0)) {
    mensagem += `, e propagados para ${pluralQuestoes(
      comGri
    )} em questionários existentes`;
  }

  // AD-7: o que não foi alcançado nunca some em silêncio — é o que o gestor
  // precisa saber para dar a passada manual.
  const semIrma = propagacao?.templates_sem_irma ?? [];
  if (semIrma.length > 0) {
    mensagem += `. ${pluralQuestoes(
      semIrma.length
    )} não foram encontradas em outras versões do template`;
  }

  // EG-42 / AD-12: mudar a forma de um questionário muda o MODELO DE NOTA do
  // certificado — cutoff global passa a cutoff por pilar
  // (certificate.service.ts:1764). É a consequência mais pesada que uma
  // classificação pode disparar, e ela acontece longe da tela onde o gestor
  // está. Aparecer aqui é o mínimo: quem propagou fica sabendo na hora.
  const formaAtualizada = propagacao?.questionarios_com_forma_atualizada ?? 0;
  if (formaAtualizada > 0) {
    const plural = formaAtualizada === 1 ? 'questionário' : 'questionários';
    mensagem +=
      `. ${formaAtualizada} ${plural} passaram a ser avaliados por pilar ` +
      `— confira as notas de corte por pilar`;
  }

  return mensagem;
};
